// Axonomic Labs — JS entrypoint.
//
// Keep this lean. Per docs/08- NFR-02 the initial JS budget is 150 KB
// gzipped; most pages should ship near zero. Stimulus controllers will
// land here as individual interactive surfaces (cookie banner, filters)
// need them. For now we ship three small, framework-free behaviours:
//
//   1. Theme attribute restore — honours a saved choice; otherwise
//      defers to prefers-color-scheme via CSS.
//   2. Header glass-on-scroll — toggles .is-scrolled past a threshold.
//   3. Mode toggle + mobile menu disclosure.

import "../styles/index.css";

// --- Theme restore (synchronous, before paint when possible) ------------
//
// The default layout sets data-theme="dark" on <html>. We respect a
// stored override here; otherwise we *remove* the attribute so the
// prefers-color-scheme CSS rule can pick up system preference.

const STORAGE_KEY = "axn-theme";

function readSavedTheme() {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

function writeSavedTheme(value) {
  try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
}

function applyTheme(theme) {
  if (theme === "light" || theme === "dark") {
    document.documentElement.setAttribute("data-theme", theme);
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

const saved = readSavedTheme();
if (saved === "light" || saved === "dark") applyTheme(saved);

// --- Header scroll state ------------------------------------------------

function bindHeaderScroll() {
  const header = document.querySelector("[data-axn-header]");
  if (!header) return;
  const threshold = 6;
  let ticking = false;
  const update = () => {
    header.classList.toggle("is-scrolled", window.scrollY > threshold);
    ticking = false;
  };
  update();
  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });
}

// --- Mode toggle --------------------------------------------------------
//
// Cycle dark → light. Resolves the *current* effective theme so the
// toggle does the right thing even when no override is stored.

function effectiveTheme() {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark" || attr === "light") return attr;
  const mql = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)");
  return mql && mql.matches ? "light" : "dark";
}

function bindModeToggle() {
  const btn = document.querySelector("[data-axn-mode-toggle]");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const next = effectiveTheme() === "dark" ? "light" : "dark";
    applyTheme(next);
    writeSavedTheme(next);
  });
}

// --- Mobile menu disclosure --------------------------------------------

function bindMobileMenu() {
  const toggle = document.querySelector("[data-axn-menu-toggle]");
  const drawer = document.querySelector("[data-axn-drawer]");
  if (!toggle || !drawer) return;

  const close = () => {
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open menu");
    drawer.setAttribute("aria-hidden", "true");
  };
  const open = () => {
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Close menu");
    drawer.setAttribute("aria-hidden", "false");
  };

  toggle.addEventListener("click", () => {
    toggle.getAttribute("aria-expanded") === "true" ? close() : open();
  });

  // Esc closes; resizing to desktop closes.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });
  window.matchMedia("(min-width: 960px)").addEventListener("change", (e) => {
    if (e.matches) close();
  });
}

// --- Filter pills (case-studies, insights) -----------------------------
//
// Wire once, works for any number of groups on a page. Each group's
// active filter is combined with siblings' active filters (AND semantics)
// against `[data-axn-<group>]` attributes on the target cards. The group
// name is whatever `data-axn-filter-group="…"` says.

function bindFilters() {
  const groups = document.querySelectorAll("[data-axn-filter-group]");
  const target = document.querySelector("[data-axn-filter-target]");
  if (!groups.length || !target) return;

  const activeByGroup = new Map();
  groups.forEach((g) => activeByGroup.set(g.dataset.axnFilterGroup, "all"));

  const apply = () => {
    for (const card of target.children) {
      let visible = true;
      for (const [group, value] of activeByGroup) {
        if (value === "all") continue;
        const attr = card.getAttribute(`data-axn-${group}`);
        if (!attr) { visible = false; break; }
        // Cards can carry multiple values in a space-separated list (e.g.
        // demos with data-axn-service="s0 s3 s4 s8"). Match if `value` is
        // any of the tokens.
        const tokens = attr.split(/\s+/).filter(Boolean);
        if (!tokens.includes(value)) { visible = false; break; }
      }
      card.hidden = !visible;
    }
  };

  groups.forEach((group) => {
    const groupName = group.dataset.axnFilterGroup;
    group.addEventListener("click", (e) => {
      const pill = e.target.closest(".filter-pill");
      if (!pill || !group.contains(pill)) return;
      const value = pill.dataset.axnFilter;
      if (!value) return;
      group.querySelectorAll(".filter-pill").forEach((p) => {
        const on = p === pill;
        p.classList.toggle("is-active", on);
        p.setAttribute("aria-pressed", on ? "true" : "false");
      });
      activeByGroup.set(groupName, value);
      apply();
    });
  });
}

// --- Cookie consent banner --------------------------------------------
//
// Stored under "axn-consent" with an expires-at timestamp. Choice is
// remembered for 12 months. Emits `consent_choice_made` /
// `consent_choice_changed` custom events for analytics wiring later.

const CONSENT_KEY = "axn-consent";
const CONSENT_TTL_DAYS = 365;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.expires_at || Date.now() > parsed.expires_at) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

function writeConsent(state) {
  const expires_at = Date.now() + CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000;
  const payload = { ...state, expires_at, saved_at: Date.now(), version: 1 };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch { /* noop */ }
  return payload;
}

function emitConsentEvent(name, detail) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

function bindCookieBanner() {
  const banner = document.querySelector("[data-axn-cookie-banner]");
  const prefs = document.querySelector("[data-axn-cookie-prefs]");
  if (!banner || !prefs) return;

  const existing = readConsent();
  const isFirstTime = !existing;

  const openBanner = () => { banner.hidden = false; };
  const closeBanner = () => { banner.hidden = true; };
  const openPrefs = () => {
    // Sync toggles to current state
    if (existing) {
      prefs.querySelectorAll("[data-axn-cookie-cat]").forEach((cb) => {
        cb.checked = !!existing[cb.dataset.axnCookieCat];
      });
    }
    prefs.hidden = false;
    document.body.style.overflow = "hidden";
  };
  const closePrefs = () => {
    prefs.hidden = true;
    document.body.style.overflow = "";
  };

  const setAll = (value) => ({
    analytics: value,
    booking: value,
    marketing: value,
  });

  const decide = (choice) => {
    let payload;
    if (choice === "accept") {
      payload = setAll(true);
    } else if (choice === "necessary") {
      payload = setAll(false);
    } else if (choice === "save-prefs") {
      payload = {};
      prefs.querySelectorAll("[data-axn-cookie-cat]").forEach((cb) => {
        payload[cb.dataset.axnCookieCat] = !!cb.checked;
      });
    } else {
      return;
    }
    const saved = writeConsent(payload);
    closeBanner();
    closePrefs();
    emitConsentEvent(
      isFirstTime ? "consent_choice_made" : "consent_choice_changed",
      saved
    );
  };

  banner.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-axn-cookie]");
    if (!btn) return;
    const choice = btn.dataset.axnCookie;
    if (choice === "preferences") { openPrefs(); return; }
    decide(choice);
  });

  prefs.addEventListener("click", (e) => {
    if (e.target.hasAttribute("data-axn-cookie-prefs-close")) { closePrefs(); return; }
    const btn = e.target.closest("[data-axn-cookie]");
    if (!btn) return;
    decide(btn.dataset.axnCookie);
  });

  // Re-open the prefs modal from any element with href="/cookies/#preferences"
  document.addEventListener("click", (e) => {
    const anchor = e.target.closest('a[href="/cookies/#preferences"]');
    if (!anchor) return;
    e.preventDefault();
    openPrefs();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !prefs.hidden) closePrefs();
  });

  if (isFirstTime) openBanner();
}

// --- Analytics (Plausible) --------------------------------------------
//
// Cookieless, but still consent-gated per FR-16 — only load the script
// after the analytics category is granted, and only fire events with the
// same gate. Non-consented event calls queue and drop on the floor when
// the visitor never grants; they flush when the visitor grants later.

const eventQueue = [];
let plausibleLoaded = false;

function analyticsGranted() {
  const c = readConsent();
  return !!(c && c.analytics);
}

function loadPlausibleScript() {
  if (plausibleLoaded) return;
  const domain =
    document.querySelector('meta[name="axn-analytics-domain"]')?.content;
  if (!domain) return;
  const s = document.createElement("script");
  s.src = "https://plausible.io/js/script.js";
  s.defer = true;
  s.setAttribute("data-domain", domain);
  document.head.appendChild(s);
  plausibleLoaded = true;
  // Flush queued events once the script has evaluated.
  s.addEventListener("load", () => {
    while (eventQueue.length && typeof window.plausible === "function") {
      const [name, opts] = eventQueue.shift();
      try { window.plausible(name, opts); } catch { /* noop */ }
    }
  });
}

function axnTrack(name, opts) {
  if (!name) return;
  if (typeof window.plausible === "function") {
    try { window.plausible(name, opts); } catch { /* noop */ }
  } else if (analyticsGranted()) {
    eventQueue.push([name, opts]);
    loadPlausibleScript();
  }
  // Non-consented tracking calls are silently dropped.
}

function bindAnalytics() {
  // Consent already granted before this pageload — load immediately.
  if (analyticsGranted()) loadPlausibleScript();

  // Consent granted later — load then. `consent_choice_changed` also
  // fires on re-decision so a user going analytics: false → true is
  // caught here too.
  ["consent_choice_made", "consent_choice_changed"].forEach((ev) => {
    document.addEventListener(ev, (e) => {
      if (e.detail?.analytics) loadPlausibleScript();
    });
  });

  // Thanks-page goal — fire on load if the head has a goal meta tag.
  const goal = document.querySelector('meta[name="axn-goal"]')?.content;
  if (goal) axnTrack(goal);
}

// --- Booking widget (click-to-load Cal.com embed) -----------------------
//
// GDPR-clean by construction: Cal's embed.js doesn't load until the
// visitor clicks. A bare iframe of the booking page is NOT an option —
// Cal's app detects it is framed and waits for the embed.js postMessage
// handshake, rendering a blank page forever. So on click we inject Cal's
// official loader stub (queues API calls until embed.js arrives) and let
// it build the inline widget inside our frame element.

function injectCalStub(scriptUrl) {
  // Cal.com's official embed snippet, verbatim apart from the
  // configurable script URL (we point it at the EU instance).
  /* eslint-disable */
  (function (C, A, L) {
    let p = function (a, ar) { a.q.push(ar); };
    let d = C.document;
    C.Cal = C.Cal || function () {
      let cal = C.Cal;
      let ar = arguments;
      if (!cal.loaded) {
        cal.ns = {};
        cal.q = cal.q || [];
        d.head.appendChild(d.createElement("script")).src = A;
        cal.loaded = true;
      }
      if (ar[0] === L) {
        const api = function () { p(api, arguments); };
        const namespace = ar[1];
        api.q = api.q || [];
        if (typeof namespace === "string") {
          cal.ns[namespace] = cal.ns[namespace] || api;
          p(cal.ns[namespace], ar);
          p(cal, ["initNamespace", namespace]);
        } else p(cal, ar);
        return;
      }
      p(cal, ar);
    };
  })(window, scriptUrl, "init");
  /* eslint-enable */
}

function bindBookingLoader() {
  const loader = document.querySelector("[data-axn-booking-loader]");
  if (!loader) return;
  const btn = loader.querySelector("[data-axn-booking-load]");
  const frame = loader.querySelector("[data-axn-booking-frame]");
  const { calLink, embedScript, embedOrigin } = loader.dataset;
  if (!btn || !frame || !calLink || !embedScript || !embedOrigin) return;

  btn.addEventListener("click", () => {
    injectCalStub(embedScript);
    window.Cal("init", { origin: embedOrigin });
    window.Cal("inline", {
      elementOrSelector: frame,
      calLink,
      config: { theme: effectiveTheme() },
    });
    window.Cal("on", {
      action: "bookingSuccessful",
      callback: () => axnTrack("booking_confirmed"),
    });
    frame.hidden = false;
    loader.querySelector(".booking-loader__panel")?.setAttribute("hidden", "true");
    axnTrack("booking_widget_opened");
  });
}

// --- Forms — soft nudge, plus analytics ping on submit -----------------
//
// The forms POST to Formspree / Buttondown natively; we don't intercept.
// This just fires an analytics event on submit so a bounced request that
// never lands on the thanks page still shows in the funnel.

function bindForms() {
  document.querySelectorAll("[data-axn-form]").forEach((form) => {
    form.addEventListener("submit", () => {
      const kind = form.dataset.axnForm;  // "contact" | "waitlist" | "newsletter"
      axnTrack(`${kind}_submitted`);
    });
  });
}

// --- Boot --------------------------------------------------------------

function boot() {
  bindHeaderScroll();
  bindModeToggle();
  bindMobileMenu();
  bindFilters();
  bindCookieBanner();
  bindAnalytics();
  bindBookingLoader();
  bindForms();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
